import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';
import { Helmet } from 'react-helmet';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

const AreaConversionCalculator = () => {
  const [area, setArea] = useState('');
  const [fromUnit, setFromUnit] = useState('square meters');
  const [toUnit, setToUnit] = useState('square feet');
  const [convertedArea, setConvertedArea] = useState(null);
  const [error, setError] = useState('');
  const [chartData, setChartData] = useState(null);
  const [solutionSteps, setSolutionSteps] = useState([]);

  const handleAreaChange = (e) => {
    setArea(e.target.value);
    setError('');
  };

  const handleFromUnitChange = (e) => {
    setFromUnit(e.target.value);
  };

  const handleToUnitChange = (e) => {
    setToUnit(e.target.value);
  };

  const convertArea = () => {
    if (!area) {
      setError('Please enter an area value.');
      return;
    }

    const areaValue = parseFloat(area);

    if (isNaN(areaValue)) {
      setError('Please enter a valid number.');
      return;
    }

    let convertedValue;

    const conversionFactors = {
      'square meters': {
        'square meters': 1,
        'square feet': 10.7639,
        'acres': 0.000247105,
        'hectares': 0.0001
      },
      'square feet': {
        'square meters': 0.092903,
        'square feet': 1,
        'acres': 0.0000229568,
        'hectares': 0.0000092903
      },
      'acres': {
        'square meters': 4046.86,
        'square feet': 43560,
        'acres': 1,
        'hectares': 0.404686
      },
      'hectares': {
        'square meters': 10000,
        'square feet': 107639,
        'acres': 2.47105,
        'hectares': 1
      }
    };

    convertedValue = areaValue * conversionFactors[fromUnit][toUnit];
    setConvertedArea(convertedValue.toFixed(4));

    // Update chart data
    setChartData({
      labels: [fromUnit, toUnit],
      datasets: [
        {
          label: 'Area Conversion',
          data: [areaValue, convertedValue],
          backgroundColor: ['rgba(75, 192, 192, 0.5)', 'rgba(54, 162, 235, 0.5)'],
          borderColor: ['rgba(75, 192, 192, 1)', 'rgba(54, 162, 235, 1)'],
          borderWidth: 1
        }
      ]
    });

    // Update solution steps
    setSolutionSteps([
      `Area: ${areaValue.toFixed(2)} ${fromUnit}`,
      `Conversion: ${fromUnit} to ${toUnit}`,
      `Converted Area: ${convertedValue.toFixed(4)} ${toUnit}`
    ]);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    convertArea();
  };

  return (
    <>
      <Helmet>
        <title>Area Conversion Calculator | BioLab Tools</title>
        <meta name="description" content="Convert areas between square meters, square feet, acres, and hectares with ease. Use this calculator to get accurate area conversions." />
        <meta name="keywords" content="area conversion calculator, convert area, square meters to square feet, acres to hectares" />
      </Helmet>

      <div className="min-h-screen bg-gray-50 py-8">
        <div className="container mx-auto px-4 max-w-7xl">
          <header className="mb-8 text-center">
            <div className="text-4xl font-bold text-green-600 mb-2">🔍</div>
            <h1 className="text-3xl md:text-4xl font-bold text-gray-800 mb-2">Area Conversion Calculator</h1>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Convert areas between square meters, square feet, acres, and hectares with ease. Get accurate area conversions.
            </p>
          </header>

          <div className="bg-white p-6 rounded-lg shadow-md max-w-2xl mx-auto">
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Area</label>
                <input
                  type="number"
                  value={area}
                  onChange={handleAreaChange}
                  className="w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                  placeholder="Enter area"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">From Unit</label>
                <select
                  value={fromUnit}
                  onChange={handleFromUnitChange}
                  className="w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                >
                  <option value="square meters">Square Meters</option>
                  <option value="square feet">Square Feet</option>
                  <option value="acres">Acres</option>
                  <option value="hectares">Hectares</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">To Unit</label>
                <select
                  value={toUnit}
                  onChange={handleToUnitChange}
                  className="w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                >
                  <option value="square meters">Square Meters</option>
                  <option value="square feet">Square Feet</option>
                  <option value="acres">Acres</option>
                  <option value="hectares">Hectares</option>
                </select>
              </div>

              <button
                type="submit"
                className="w-full bg-green-600 text-white py-3 px-4 rounded-md font-semibold hover:bg-green-700 transition duration-200"
              >
                Convert Area
              </button>

              {error && (
                <div className="bg-red-100 text-red-700 p-4 rounded-md mt-4">
                  {error}
                </div>
              )}

              {convertedArea !== null && (
                <div className="mt-8">
                  <h2 className="text-2xl font-bold text-gray-800 mb-6">Result</h2>
                  <div className="bg-gray-50 p-6 rounded-lg shadow-sm">
                    <p className="text-lg font-medium">{convertedArea} {toUnit}</p>
                  </div>

                  <div className="mt-8">
                    <h3 className="text-xl font-bold text-gray-800 mb-4">Visualization</h3>
                    <div className="h-96">
                      <Bar
                        data={chartData}
                        options={{
                          responsive: true,
                          maintainAspectRatio: false,
                          scales: {
                            y: {
                              beginAtZero: true,
                              title: { display: true, text: 'Area' }
                            },
                            x: { title: { display: true, text: 'Unit' } }
                          },
                          plugins: { title: { display: true, text: 'Area Conversion' } }
                        }}
                      />
                    </div>
                  </div>

                  <div className="mt-8">
                    <h3 className="text-xl font-bold text-gray-800 mb-4">Solution Steps</h3>
                    <div className="bg-gray-50 p-6 rounded-lg shadow-sm">
                      <ul className="list-disc pl-6 space-y-2">
                        {solutionSteps.map((step, index) => (
                          <li key={index} className="text-gray-600">{step}</li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              )}
            </form>
          </div>

          {/* FAQ Section */}
          <div className="mt-16 bg-white p-6 rounded-lg shadow-md max-w-4xl mx-auto">
            <h2 className="text-2xl font-bold text-gray-800 mb-4">Frequently Asked Questions (FAQ)</h2>
            <div className="space-y-6">
              <div className="p-6 bg-gray-50 rounded-lg">
                <h3 className="text-xl font-semibold text-blue-600 mb-4">What is area conversion?</h3>
                <p className="text-gray-600">
                  Area conversion is the process of converting an area value from one unit to another, such as from square meters to square feet or acres to hectares.
                </p>
              </div>

              <div className="p-6 bg-gray-50 rounded-lg">
                <h3 className="text-xl font-semibold text-blue-600 mb-4">How does this calculator work?</h3>
                <p className="text-gray-600">
                  This calculator uses standard conversion factors to convert areas between different units.
                </p>
              </div>

              <div className="p-6 bg-gray-50 rounded-lg">
                <h3 className="text-xl font-semibold text-blue-600 mb-4">Can I use this calculator for scientific purposes?</h3>
                <p className="text-gray-600">
                  Yes, this calculator is accurate and suitable for scientific purposes, helping you convert areas between different units.
                </p>
              </div>
            </div>
          </div>

          {/* How to Use Section */}
          <div className="mt-16 bg-white p-6 rounded-lg shadow-md max-w-4xl mx-auto">
            <h2 className="text-2xl font-bold text-gray-800 mb-4">How to Use the Area Conversion Calculator</h2>
            <div className="space-y-8">
              <div className="p-6 bg-gray-50 rounded-lg">
                <h3 className="text-xl font-semibold text-blue-600 mb-4">Step 1: Enter Area</h3>
                <p className="text-gray-600">
                  Input the area value you want to convert.
                </p>
              </div>

              <div className="p-6 bg-gray-50 rounded-lg">
                <h3 className="text-xl font-semibold text-blue-600 mb-4">Step 2: Select From Unit</h3>
                <p className="text-gray-600">
                  Choose the unit of the area you are converting from.
                </p>
              </div>

              <div className="p-6 bg-gray-50 rounded-lg">
                <h3 className="text-xl font-semibold text-blue-600 mb-4">Step 3: Select To Unit</h3>
                <p className="text-gray-600">
                  Choose the unit you want to convert the area to.
                </p>
              </div>

              <div className="p-6 bg-gray-50 rounded-lg">
                <h3 className="text-xl font-semibold text-blue-600 mb-4">Step 4: Convert Area</h3>
                <p className="text-gray-600">
                  Click the "Convert Area" button to get the result.
                </p>
              </div>

              <div className="p-6 bg-gray-50 rounded-lg">
                <h3 className="text-xl font-semibold text-blue-600 mb-4">Step 5: View Result and Visualization</h3>
                <p className="text-gray-600">
                  The result will be displayed, along with a bar chart visualizing the conversion.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default AreaConversionCalculator;